import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronUp, Minus, X, Play, Square, RotateCcw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Pill } from '@/components/ui/Pill';
import { ApiClientError } from '@/lib/apiClient';
import { formatCurrencyEurRo, formatElapsedRo } from '@/lib/format';
import { tierLabel } from '@/lib/tiers';
import { useMe } from '@/lib/useMe';
import { canSeeBilling } from '@/lib/permissions';
import { useActiveSession, useStartSession, useStopSession, useWallet } from '@/lib/walletHooks';
import { useSessionDocked, useSetSessionDrawerPhase } from './sessionDrawerPresence';
import { useAutoDockOnInteraction } from './useAutoDock';

const DISMISS_KEY = 'lyra:session-prompt-dismissed';

/** How long the drawer holds the corner before the operator's next action
 *  elsewhere minimizes it — long enough to read the boot state, short enough
 *  that it never sits in the way of real work. */
const AUTO_DOCK_DELAY_MS = 4000;

// Gasket install phases -> Romanian. Unknown/absent phase falls back to a
// generic „starting" line (the phase can be absent before the box first reports).
const PHASE_LABEL: Record<string, string> = {
  booting: 'Se inițializează mașina…',
  pulling: 'Se descarcă din registru…',
  downloading: 'Se descarcă modelele…',
  creating: 'Se pregătește modelul…',
  ready: 'Aproape gata…',
};
function phaseLabel(phase?: string): string {
  return (phase && PHASE_LABEL[phase]) || 'Se pornește instanța…';
}

/**
 * Global session surface, docked in the bottom-right corner. On login (no
 * active session) it shows a NON-MODAL prompt card offering to start a
 * processing instance; on „Pornește" the same corner card becomes a live
 * status drawer (provisioning → active), minimizable to a pill. If a session
 * is already running at login it opens straight to the drawer.
 *
 * 2026-08-21 UI report — the drawer also DOCKS: minimized (by its ⌃ button, or
 * automatically once the operator has ignored it for `AUTO_DOCK_DELAY_MS` and
 * acted elsewhere on the page), it stops painting the corner entirely and
 * `DocTop`'s `SessionChip` carries the state in the line that opens every
 * page. Clicking the chip brings this card back. The docked flag lives in
 * `sessionDrawerPresence` because both ends need it.
 *
 * Deliberately NOT a modal (DESIGN.md „no modal layer — inline-first"): no
 * full-screen scrim, no `role="dialog"`, no focus trap — it never blocks the
 * app or steals focus. It is dismissible with Escape and announces its own
 * boot state on a single polite live region (so the a11y pass elsewhere must
 * not re-announce the same transitions). Prompt and drawer share one corner
 * anchor, so there is no center→corner morph — only a short width tween within
 * the 150–250ms state-feedback envelope. Money (rate/cost) shows ONLY to
 * billing viewers (billing-privacy); members get qualitative language.
 */
export function SessionDrawer() {
  const { data: me } = useMe();
  const billing = canSeeBilling(me);
  const { data: active } = useActiveSession();
  const { data: wallet } = useWallet();
  const start = useStartSession();
  const stop = useStopSession();

  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === '1');
  const [justStarted, setJustStarted] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [promptExpanded, setPromptExpanded] = useState(false); // prompt FAB → full offer
  const [hidden, setHidden] = useState(false); // user closed the drawer entirely
  // Minimized into `DocTop`'s chip (2026-08-21 UI report). Lives in the shared
  // presence context, not here, because `SessionChip` — a different subtree —
  // both reads it and clears it on click.
  const [docked, setDocked] = useSessionDocked();
  /** The drawer's own root — interactions inside it are working WITH the
   *  drawer, so `useAutoDockOnInteraction` must not read them as „moved on". */
  const cardRef = useRef<HTMLDivElement>(null);

  const hasSession = Boolean(active);
  // Once the real session appears, the optimistic flag is no longer needed.
  useEffect(() => { if (active) setJustStarted(false); }, [active]);

  // A brand-new session (e.g. auto-started by a run restart, or from Panou)
  // should surface the drawer even if the user had closed it for a previous
  // one — otherwise „Repornire" would silently rent with no visible drawer.
  const prevSessionIdRef = useRef<string | null>(null);
  useEffect(() => {
    const id = active?.id ?? null;
    if (id && id !== prevSessionIdRef.current) {
      setHidden(false);
      // A brand-new session also un-docks: „Repornire" must not land silently
      // in a chip the operator has to notice on their own.
      setDocked(false);
    }
    prevSessionIdRef.current = id;
  }, [active?.id, setDocked]);

  const state = active?.provisioningState ?? null;
  const provisioning = state === 'provisioning' || (justStarted && !active && start.isPending);
  const ready = Boolean(active) && (state === 'ready' || state === null);
  const startFailed = start.isError && !active;
  // Rule 1: a present-but-`percent`-less progress step is NOT 0% — treat a
  // missing/non-finite percent as null so the bar renders indeterminate.
  const pct = active?.progress?.percent;
  const progressPercent = typeof pct === 'number' && Number.isFinite(pct) ? pct : null;

  const phase: 'prompt' | 'drawer' | 'docked' | 'off' = useMemo(() => {
    if (hidden) return 'off';
    if (hasSession || justStarted || startFailed) return docked ? 'docked' : 'drawer';
    return dismissed ? 'off' : 'prompt';
  }, [hidden, hasSession, justStarted, startFailed, dismissed, docked]);

  // „trebuie minimizata daca userul o ignora si face actiuni pe site" — once
  // the card has had a few seconds of undivided attention, the next click or
  // keystroke anywhere else minimizes it into `DocTop`'s chip. Only armed for
  // a real, non-failed session drawer: the „Pornire eșuată" card carries a
  // „Reîncearcă" the operator still has to answer, and the prompt already has
  // its own dismiss path (Escape / „Nu acum").
  useAutoDockOnInteraction({
    enabled: phase === 'drawer' && !startFailed,
    delayMs: AUTO_DOCK_DELAY_MS,
    onDock: () => setDocked(true),
    ignoreWithin: cardRef,
  });

  // Publish the corner phase so layouts can react per-phase — notably, the
  // ResultsTable gutter reserves space only for the full `drawer` card, NOT the
  // slimmed `prompt` FAB (which doesn't cover STATUS). Cleared on unmount.
  const setPhase = useSetSessionDrawerPhase();
  useEffect(() => {
    setPhase(phase);
    return () => setPhase('off');
  }, [phase, setPhase]);

  function dismiss() {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  }
  function onStart() {
    start.reset();
    setJustStarted(true);
    setCollapsed(false);
    start.mutate();
  }
  function onStop() {
    if (!active) return;
    stop.mutate(active.id, {
      onSuccess: () => { setJustStarted(false); dismiss(); },
    });
  }

  // Escape dismisses the (non-modal) prompt — the one keyboard affordance a
  // proactive corner offer needs. It traps nothing, so there is no focus to
  // restore. Logic inlined (not `dismiss`) to keep the effect deps empty.
  const isPromptPhase = phase === 'prompt';
  useEffect(() => {
    if (!isPromptPhase) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        sessionStorage.setItem(DISMISS_KEY, '1');
        setDismissed(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isPromptPhase]);

  // Docked → `DocTop`'s `SessionChip` is painting the state instead; the
  // corner stays empty (and layouts drop their drawer gutter, since that
  // gates on `phase === 'drawer'`).
  if (phase === 'off' || phase === 'docked') return null;

  const isPrompt = phase === 'prompt';

  // Prompt, not yet opened → a compact corner FAB instead of the full ~352px
  // card. Deliberately small (size-12) so it covers only the results grid's
  // trailing actions column, never STATUS — the persistent prompt-over-STATUS
  // occlusion is exactly what the drawer gutter existed to prevent, so slimming
  // it here lets the gutter drop in this phase and the grid keep its full width.
  // Clicking opens the full offer below (transient, user-initiated).
  if (isPrompt && !promptExpanded) {
    return (
      <button
        type="button"
        onClick={() => setPromptExpanded(true)}
        title="Pornește o instanță de procesare"
        aria-label="Pornește o instanță de procesare"
        className="fixed bottom-5 right-4 z-50 flex size-12 items-center justify-center rounded-full bg-teal text-teal-ink shadow-lg ring-1 ring-black/10 motion-safe:transition hover:brightness-105"
      >
        <Play className="size-5 fill-current" />
      </button>
    );
  }

  // Concise, polite announcement of instance-boot state. Owned here (a single
  // live region) so a separate a11y pass doesn't double-announce the same
  // transitions.
  const announcement = startFailed
    ? 'Pornirea instanței a eșuat.'
    : ready
      ? 'Instanța de calcul este pornită.'
      : provisioning
        ? 'Se pornește instanța de procesare.'
        : '';

  const width = isPrompt
    ? 'w-[min(22rem,calc(100vw-2rem))]'
    : collapsed
      ? 'w-56'
      // Clamp to the viewport too: the fixed drawer sits outside the clipped
      // content column, so a bare 22rem overhangs the left edge on a ≤360px phone.
      : 'w-[min(22rem,calc(100vw-2rem))]';

  return (
    <div
      ref={cardRef}
      aria-label="Sesiune de procesare"
      className={`fixed bottom-5 right-4 z-50 overflow-hidden rounded-xl border border-rule bg-bg shadow-lg motion-safe:transition-[width] motion-safe:duration-200 ${width}`}
    >
      {/* Single polite live region for instance-boot state. */}
      <span className="sr-only" role="status" aria-live="polite">{announcement}</span>

      {isPrompt ? (
        <div className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="text-[11px] font-bold uppercase tracking-[0.07em] text-ink-3">Sesiune de procesare</div>
            <button
              type="button"
              onClick={() => setPromptExpanded(false)}
              title="Restrânge"
              aria-label="Restrânge"
              className="-mr-1 -mt-1 flex size-6 shrink-0 items-center justify-center rounded-md text-ink-3 hover:bg-rule-soft hover:text-ink-2"
            >
              <Minus className="size-4" />
            </button>
          </div>
          <h2 className="mt-1.5 text-[16px] font-[650] tracking-tight text-ink">Pornești o instanță?</h2>
          <p className="mt-1.5 text-[13px] leading-relaxed text-ink-2">
            Instanța de calcul clasifică mapările. Pornirea consumă credite din portofelul organizației și se oprește automat după 30 de minute de inactivitate.
          </p>
          {billing && (
            <div className="mt-3 inline-flex items-baseline gap-1.5 rounded-lg border border-rule bg-wash px-3 py-1.5">
              <b className="text-[16px] font-[650] tabular-nums text-ink">
                {wallet ? `${formatCurrencyEurRo(wallet.hourlyPriceEur)}` : '—'}
              </b>
              <span className="text-[12px] text-ink-3">/oră · minim 1 oră</span>
            </div>
          )}
          <div className="mt-4 flex gap-2.5">
            <Button onClick={onStart} disabled={start.isPending}>
              {start.isPending ? <Loader2 className="motion-safe:animate-spin" /> : <Play className="fill-current" />}
              {start.isPending ? 'Se pornește…' : 'Pornește instanța'}
            </Button>
            <button
              type="button"
              onClick={dismiss}
              className="rounded-lg px-3 text-[13px] font-medium text-ink-3 hover:text-ink-2"
            >
              Nu acum
            </button>
          </div>
        </div>
      ) : (
        <>
          {!collapsed && (
            <div className="flex items-center gap-2.5 border-b border-rule-soft px-3.5 py-2.5">
              {startFailed ? (
                <Pill variant="bad">Eroare</Pill>
              ) : provisioning ? (
                <Pill variant="warn">Se pornește</Pill>
              ) : ready ? (
                <Pill variant="accent">Activă</Pill>
              ) : (
                <Pill variant="mute">Instanță</Pill>
              )}
              <span className="truncate text-[13.5px] font-semibold text-ink">
                {ready ? 'Instanță activă' : startFailed ? 'Pornire eșuată' : 'Instanță de procesare'}
              </span>
              <span className="ml-auto flex gap-0.5">
                {!startFailed && (
                  <>
                    <button
                      type="button"
                      onClick={() => setDocked(true)}
                      title="Mută în bara de sus"
                      aria-label="Mută în bara de sus"
                      className="flex size-6 items-center justify-center rounded-md text-ink-3 hover:bg-rule-soft hover:text-ink-2"
                    >
                      <ChevronUp className="size-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setCollapsed((v) => !v)}
                      title={collapsed ? 'Extinde' : 'Restrânge'}
                      aria-label={collapsed ? 'Extinde' : 'Restrânge'}
                      className="flex size-6 items-center justify-center rounded-md text-ink-3 hover:bg-rule-soft hover:text-ink-2"
                    >
                      <Minus className="size-4" />
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => setHidden(true)}
                  title="Închide"
                  aria-label="Închide"
                  className="flex size-6 items-center justify-center rounded-md text-ink-3 hover:bg-rule-soft hover:text-ink-2"
                >
                  <X className="size-4" />
                </button>
              </span>
            </div>
          )}

          {!collapsed && (
            <div className="p-3.5">
              {startFailed ? (
                <>
                  <p className="text-[13px] text-bad">
                    {start.error instanceof ApiClientError ? start.error.message : 'Nu s-a putut porni instanța.'}
                  </p>
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" onClick={onStart} disabled={start.isPending}>
                      <RotateCcw />Reîncearcă
                    </Button>
                    <button type="button" onClick={() => setHidden(true)} className="text-[13px] font-medium text-ink-3 hover:text-ink-2">
                      Închide
                    </button>
                  </div>
                </>
              ) : provisioning ? (
                <>
                  <p className="text-[15px] font-[600] text-ink">{phaseLabel(active?.progress?.phase)}</p>
                  {/* Rule 1: no `percent` (registry pull / ollama create) -> an
                      INDETERMINATE bar, never an empty 0% one. Rule 2: no
                      `progress` object at all -> same indeterminate bar. Under
                      reduced motion the pulsing indeterminate bar would freeze
                      into a meaningless 1/3 block, so it's hidden there and the
                      phase + step text below carries the liveness instead. */}
                  <div
                    className={`mt-2 h-1.5 overflow-hidden rounded-full bg-rule-soft ${progressPercent == null ? 'motion-reduce:hidden' : ''}`}
                    role="progressbar"
                    aria-label="Progres pornire instanță"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={progressPercent != null ? Math.round(progressPercent) : undefined}
                  >
                    {progressPercent != null ? (
                      <div
                        className="h-full rounded-full bg-warn motion-safe:transition-[width] motion-safe:duration-500"
                        style={{ width: `${Math.max(3, Math.min(100, progressPercent))}%` }}
                      />
                    ) : (
                      <div className="h-full w-1/3 rounded-full bg-warn motion-safe:animate-pulse" />
                    )}
                  </div>
                  <p className="mt-2 text-[12.5px] leading-relaxed text-ink-3">
                    {active?.progress?.step != null && active?.progress?.steps_total != null ? (
                      <>Pasul <span className="tabular-nums text-ink-2">{active.progress.step}</span>/<span className="tabular-nums">{active.progress.steps_total}</span></>
                    ) : (
                      <>Se pregătește instanța</>
                    )}
                    {progressPercent != null && <> · <span className="tabular-nums text-ink-2">{Math.round(progressPercent)}%</span></>}
                    {billing && wallet ? <> · <span className="tabular-nums">{formatCurrencyEurRo(wallet.hourlyPriceEur)}</span>/oră</> : <> · consumă credite</>}
                    {active?.progress?.artifact ? (
                      <span className="mt-0.5 block truncate">{active.progress.artifact}</span>
                    ) : (
                      <><br />Maparea începe automat când e gata.</>
                    )}
                  </p>
                </>
              ) : (
                <>
                  {billing && active?.costEur !== undefined ? (
                    <div className="text-[26px] font-bold leading-none tracking-tight tabular-nums text-ink">
                      {formatCurrencyEurRo(active.costEur)}
                    </div>
                  ) : (
                    <div className="text-[15px] font-[600] text-ink">Instanță activă</div>
                  )}
                  <p className="mt-2 text-[12.5px] leading-relaxed text-ink-3">
                    Activă de <span className="font-medium tabular-nums text-ink-2">{formatElapsedRo(active?.elapsedMs ?? 0)}</span>
                    {' · '}plan {tierLabel(active?.tierId)}
                    {billing && active?.hourlyPriceEur !== undefined && <> · <span className="tabular-nums">{formatCurrencyEurRo(active.hourlyPriceEur)}</span>/oră</>}
                    <br />Se oprește automat după 30 min de inactivitate.
                  </p>
                  <button
                    type="button"
                    onClick={onStop}
                    disabled={stop.isPending}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-rule px-3 py-1.5 text-[12.5px] font-medium text-ink-2 hover:border-bad hover:text-bad disabled:opacity-50"
                  >
                    <Square className="size-3 fill-current" />
                    {stop.isPending ? 'Se oprește…' : 'Oprește instanța'}
                  </button>
                </>
              )}
            </div>
          )}
          {collapsed && (
            <button type="button" onClick={() => setCollapsed(false)} className="flex w-full items-center gap-2.5 px-3.5 py-3 text-left">
              {ready ? <Pill variant="accent">Activă</Pill> : <Pill variant="warn">Se pornește</Pill>}
              <span className="ml-auto truncate text-[12.5px] font-medium text-ink-2">{tierLabel(active?.tierId)}</span>
            </button>
          )}
        </>
      )}
    </div>
  );
}
